import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Button,
  LinearProgress,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  statsCard: {
    height: '100%',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4]
    }
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3)
  },
  errorSection: {
    backgroundColor: theme.palette.error.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3)
  },
  actionButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    lineHeight: 1.2
  },
  successText: {
    color: theme.palette.success.main
  },
  warningText: {
    color: theme.palette.warning.main
  },
  errorText: {
    color: theme.palette.error.main
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200
  }
}));

const FitmentDashboard = () => {
  const classes = useStyles();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, message: '' });

  const fetchStatus = async () => {
    try {
      const response = await fetch('/api/shop/fitment-status');
      const data = await response.json();
      setStatus(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching status:', error);
      setLoading(false);
      showSnackbar('Error loading fitment status', 'error');
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const runMigration = async (options = {}) => {
    setRunning(true);
    setConfirmDialog({ open: false });
    
    try {
      const response = await fetch('/api/shop/run-fitment-migration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        showSnackbar(
          'Migration started in background. Check server logs for progress.', 
          'info'
        );
        // Don't wait for completion, just refresh status periodically
        setTimeout(fetchStatus, 10000);
      } else {
        showSnackbar('Failed to start migration', 'error');
      }
    } catch (error) {
      showSnackbar('Error: ' + error.message, 'error');
    } finally {
      setRunning(false);
    }
  };
  
  const runMigrationOld = async (options = {}) => {
    setRunning(true);
    setConfirmDialog({ open: false });
    
    try {
      
      const response = await fetch('/api/shop/run-fitment-migration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options)
      });
      
      if (response.ok) {
        showSnackbar('Migration started! Check server logs for progress.', 'success');
        // Refresh status after a delay
        setTimeout(fetchStatus, 5000);
      } else {
        showSnackbar('Failed to start migration', 'error');
      }
    } catch (error) {
      showSnackbar('Error: ' + error.message, 'error');
    } finally {
      setRunning(false);
    }
  };

  const handleActionClick = (action, message) => {
    setConfirmDialog({
      open: true,
      action,
      message
    });
  };

  const handleConfirmAction = () => {
    const { action } = confirmDialog;
    runMigration(action);
  };

  if (loading) {
    return (
      <Paper className={classes.root}>
        <Box className={classes.loadingBox}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            Loading fitment status...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!status) {
    return (
      <Paper className={classes.root}>
        <Alert severity="error">Error loading fitment status</Alert>
      </Paper>
    );
  }

  const getCoverageColor = () => {
    if (status.coveragePercentage > 80) return 'success';
    if (status.coveragePercentage > 50) return 'warning';
    return 'error';
  };

  const progressColor = getCoverageColor();

  return (
    <Paper className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Fitment Data Status
          </Typography>
          <Typography variant="body2" color="textSecondary">
            O'Reilly vehicle compatibility data coverage
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchStatus} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} md={4}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Items
              </Typography>
              <Typography className={classes.statNumber}>
                {status?.totalItems?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                With Fitment Data
              </Typography>
              <Typography className={`${classes.statNumber} ${classes.successText}`}>
                {status?.itemsWithFitment?.toLocaleString()}
                <Chip 
                  label={`${status.coveragePercentage}%`}
                  color="primary"
                  size="small"
                  style={{ marginLeft: 8 }}
                />
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Without Fitment
              </Typography>
              <Typography className={classes.statNumber}>
                {status?.itemsWithoutFitment?.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Progress Bar */}
      <Box marginBottom={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="textSecondary">
            Coverage Progress
          </Typography>
          <Typography 
            variant="h6" 
            color={progressColor}
            className={progressColor === 'success' ? classes.successText : 
                       progressColor === 'warning' ? classes.warningText : 
                       classes.errorText}
          >
            {status.coveragePercentage}%
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={status.coveragePercentage} 
          className={classes.progressBar}
          color={progressColor === 'error' ? 'secondary' : progressColor}
        />
      </Box>

      {/* Errors Section */}
      {status.errors && status.errors.totalErrors > 0 && (
        <Alert 
          severity="error" 
          icon={<ErrorIcon />}
          style={{ marginBottom: 24 }}
        >
          <AlertTitle>Fitment Errors Detected</AlertTitle>
          
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Total Errors:</strong> {status.errors.totalErrors}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>Multiple Attempts:</strong> {status.errors.itemsWithMultipleAttempts}
              </Typography>
            </Grid>
          </Grid>
          
          {status.errors.topErrors && status.errors.topErrors.length > 0 && (
            <Box marginTop={2}>
              <Typography variant="subtitle2" gutterBottom>
                Top Errors:
              </Typography>
              <List dense>
                {status.errors.topErrors.map((err, idx) => (
                  <ListItem key={idx}>
                    <ListItemText 
                      primary={err.error}
                      secondary={`${err.count} items affected`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Alert>
      )}

      <Divider style={{ marginBottom: 24 }} />

      {/* Action Buttons */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Migration Actions
        </Typography>
        
        <Box display="flex" flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<PlayIcon />}
            onClick={() => handleActionClick(
              { limit: 10, dryRun: true },
              'Run a test migration on 10 items (dry run - no changes will be made)?'
            )}
            disabled={running}
            className={classes.actionButton}
          >
            Test (10 items, dry run)
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayIcon />}
            onClick={() => handleActionClick(
              { limit: 50, skipErrors: true },
              'Process 50 new items (skipping any with previous errors)?'
            )}
            disabled={running}
            className={classes.actionButton}
          >
            Process 50 New Items
          </Button>
          
          {status.errors && status.errors.totalErrors > 0 && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={() => handleActionClick(
                { retryErrors: true, limit: 20 },
                'Retry 20 items that previously failed?'
              )}
              disabled={running}
              className={classes.actionButton}
            >
              Retry 20 Failed Items
            </Button>
          )}
          
          <Button
            variant="contained"
            style={{ backgroundColor: '#4caf50', color: 'white' }}
            startIcon={<CheckCircleIcon />}
            onClick={() => handleActionClick(
              { skipErrors: true },
              'Process ALL remaining items without fitment data? This may take a long time.'
            )}
            disabled={running}
            className={classes.actionButton}
          >
            Process All Remaining
          </Button>
        </Box>
      </Box>
      
      {running && (
        <Alert 
          severity="info" 
          icon={<CircularProgress size={20} />}
          style={{ marginTop: 16 }}
        >
          Migration running... Check server logs for progress.
        </Alert>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirm Migration</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })} 
            color="default"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmAction} 
            color="primary" 
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default FitmentDashboard;
